"use client";

import { forwardRef, useId } from "react";
import { cn } from "@/lib/utils";

interface ToggleProps extends Omit<React.ComponentProps<"input">, "type" | "size"> {
  label?: string;
  /** Size of the toggle track */
  size?: "sm" | "md";
}

const Toggle = forwardRef<HTMLInputElement, ToggleProps>(
  ({ className, label, size = "md", id: externalId, ...props }, ref) => {
    const autoId = useId();
    const id = externalId || autoId;

    const trackSize = size === "sm" ? "h-5 w-9" : "h-6 w-11";
    const thumbSize =
      size === "sm"
        ? "size-3.5 peer-checked:translate-x-4"
        : "size-4 peer-checked:translate-x-5";

    // Wrap the input + visuals in a single <label> so the whole surface is
    // the click target, then make the track and thumb direct siblings of the
    // peer <input>. Tailwind's peer-* only reaches siblings, so the previous
    // structure (thumb nested inside the track) left both the click target
    // broken and the thumb sliding dead.
    const trackHeightPx = size === "sm" ? 20 : 24;
    const thumbPx = size === "sm" ? 14 : 16;
    const inset = (trackHeightPx - thumbPx) / 2;
    const thumbTranslate =
      size === "sm" ? "peer-checked:translate-x-4" : "peer-checked:translate-x-5";

    return (
      <div className="flex items-center gap-[var(--space-2)]">
        <label
          htmlFor={id}
          className="relative inline-flex items-center cursor-pointer"
        >
          <input
            ref={ref}
            id={id}
            type="checkbox"
            role="switch"
            data-slot="toggle"
            className={cn("peer sr-only", className)}
            {...props}
          />
          <span
            aria-hidden="true"
            className={cn(
              "rounded-full border border-transparent",
              "bg-[var(--color-border-default)]",
              "peer-checked:bg-[var(--color-primary)]",
              "peer-focus-visible:ring-[3px] peer-focus-visible:ring-[var(--color-primary-muted)]",
              "peer-disabled:opacity-50",
              "transition-colors duration-[var(--duration-normal)]",
              "block",
              trackSize,
            )}
          />
          <span
            aria-hidden="true"
            className={cn(
              "absolute rounded-full bg-white shadow-sm",
              "transition-transform duration-[var(--duration-normal)]",
              "peer-disabled:opacity-80",
              size === "sm" ? "size-3.5" : "size-4",
              thumbTranslate,
            )}
            style={{ top: inset, left: inset }}
          />
        </label>
        {label && (
          <label
            htmlFor={id}
            className="cursor-pointer text-[length:var(--font-size-sm)] text-[var(--color-text-primary)] select-none peer-disabled:pointer-events-none peer-disabled:opacity-50"
          >
            {label}
          </label>
        )}
      </div>
    );
  }
);
Toggle.displayName = "Toggle";

export { Toggle };
export type { ToggleProps };
